import { TFunction } from "i18next";
import {
  BACKEND_MESSAGE_MAP,
  BackendMessageKey,
} from "@/constants/backendMessages";

/**
 * Backend error response structure
 * IMPORTANT: Backend always returns messages in English in the 'detail' field.
 * We ONLY use 'error_key' and 'params' to get the translated message from our i18n system.
 * The 'detail' field is only used as a last resort fallback if error_key is missing.
 */
export interface BackendErrorResponse {
  error_key?: string; // Primary: Used to lookup translation (will be validated against BackendMessageKey)
  params?: Record<string, any>; // Primary: Used for parameter interpolation
  detail?: string; // Fallback only: English message from backend
  message?: string; // Fallback only: Alternative error field
  error?: string; // Fallback only: Alternative error field
}

/**
 * Translates a backend error response using the i18n translation system
 *
 * HOW IT WORKS:
 * 1. Backend returns: { error_key: "ERROR_ACCOUNT_LOCKED", params: { remaining_time: 15 }, detail: "Account locked..." }
 * 2. We use error_key to find the translation key in BACKEND_MESSAGE_MAP
 * 3. We get the translated message from i18n based on user's language
 * 4. We interpolate params into the translated message
 * 5. User sees message in their language (not English from 'detail')
 *
 * @param response - The backend error response object (must have error_key for translation)
 * @param t - The i18next translation function
 * @param fallback - Fallback message if translation key not found
 * @returns Translated and interpolated error message in user's language
 */
function translateBackendMessage(
  response: BackendErrorResponse,
  t: TFunction,
  fallback?: string
): string {
  // PRIMARY PATH: Use error_key to get translated message
  if (response.error_key && response.error_key in BACKEND_MESSAGE_MAP) {
    const messageConfig =
      BACKEND_MESSAGE_MAP[response.error_key as BackendMessageKey];
    const translationKey = messageConfig.translationKey;

    // If message has params, interpolate them into the translated message
    if (messageConfig.hasParams && response.params) {
      // Example: t("common.backendMessages.errors.accountLocked", { remaining_time: 15 })
      // Returns: "Account temporarily locked... Try again in 15 minute(s)." (in user's language)
      return String(t(translationKey, response.params));
    }

    // No params needed, just get the translated message
    return String(t(translationKey));
  }

  // FALLBACK PATH: Only used if backend doesn't send error_key
  // Note: These will be in English since backend returns English messages
  if (response.detail && typeof response.detail === "string") {
    return response.detail;
  }

  if (response.message) {
    return response.message;
  }

  if (response.error) {
    return response.error;
  }

  // Final fallback
  return fallback || "An unexpected error occurred";
}

export { translateBackendMessage };
